// src/pages/league-dashboard/live-draft/components/VirtualAvailablePlayersTable.js
// Virtual scrolling table for available players in live draft - 60x performance improvement
import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { FixedSizeList } from 'react-window';
import { ChevronUp, ChevronDown, ChevronsUpDown, RefreshCw, AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { leaguesAPI } from '../../../../services/apiService';
import { createDraftAvailableColumns } from '../../../../services/tables/draftColumns';
import { getNestedValue } from '../../../../utils/getNestedValue';
import { isRawPitcherPosition } from '../../../../utils/playerClassification';

const VirtualAvailablePlayersTable = React.memo(({
  leagueId,
  filters,
  onPlayerSelect,
  onViewProfile,
  onAddToQueue,
  draftedPlayerIds = [],
  onPlayersLoaded,
  currentSeason,
  league = null,
  isYourTurn = false,
  selectedPlayerId = null,
  refreshTrigger = 0
}) => {
  const listRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Use refs for values that shouldn't trigger re-fetching
  const draftedIdsRef = useRef(draftedPlayerIds);
  const onPlayersLoadedRef = useRef(onPlayersLoaded);
  const loadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const lastLoadParamsRef = useRef(null);

  // Update refs when props change (but don't trigger re-render)
  useEffect(() => {
    draftedIdsRef.current = draftedPlayerIds;
  }, [draftedPlayerIds]);

  useEffect(() => {
    onPlayersLoadedRef.current = onPlayersLoaded;
  }, [onPlayersLoaded]);

  // State
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');
  // Default to previous season stats during offseason (setup/draft/finalizing) — mirrors get_stats_season()
  const isOffseason = ['setup', 'draft', 'finalizing'].includes(league?.league_status);
  const [statsYear, setStatsYear] = useState(isOffseason ? 'previous' : 'current');
  const [sortConfig, setSortConfig] = useState({
    key: 'baseline_price',
    direction: 'desc'
  });

  // Calculate previous season year
  const previousSeason = currentSeason - 1;

  // Get scoring categories from league settings
  const scoringCategories = useMemo(() => {
    if (league?.league_settings?.scoring_categories) {
      return {
        hitting: league.league_settings.scoring_categories.hitting || null,
        pitching: league.league_settings.scoring_categories.pitching || null
      };
    }
    return { hitting: null, pitching: null };
  }, [league]);

  // Extract STABLE filter values to avoid object reference issues
  const filterPosition = filters?.position;
  const filterSearch = filters?.search;
  const filterSortBy = filters?.sortBy;
  const filterSortOrder = filters?.sortOrder;
  // Hitter filters
  const filterMinG = filters?.minG || 0;
  const filterMinAB = filters?.minAB || 0;
  // Pitcher filters
  const filterMinGPitching = filters?.minGPitching || 0;
  const filterMinGS = filters?.minGS || 0;
  const filterMinIP = filters?.minIP || 0;

  // Load players - only depends on STABLE primitive values
  const loadPlayers = useCallback(async () => {
    // Prevent concurrent loads
    if (loadingRef.current) return;

    // Create params key to check if params actually changed
    const paramsKey = `${leagueId}|${filterPosition}|${filterSearch}|${filterSortBy}|${filterSortOrder}`;

    // Skip if params haven't changed (prevents duplicate calls during rapid re-renders)
    if (lastLoadParamsRef.current === paramsKey && initialLoadDoneRef.current) {
      return;
    }

    loadingRef.current = true;
    lastLoadParamsRef.current = paramsKey;

    try {
      setLoading(true);

      // Load ALL players by making multiple API calls (backend limit is 500)
      let allPlayers = [];
      let offset = 0;
      const batchSize = 500;
      let hasMore = true;

      while (hasMore) {
        const response = await leaguesAPI.getAvailablePlayersForDraft(leagueId, {
          position: filterPosition !== 'ALL' ? filterPosition : null,
          search: filterSearch || null,
          sort_by: filterSortBy || 'baseline_price',
          sort_order: filterSortOrder || 'desc',
          limit: batchSize,
          offset: offset
        });

        const batch = response.players || response.data?.players || [];
        allPlayers = [...allPlayers, ...batch];

        // If we got fewer than batchSize, we've reached the end
        if (batch.length < batchSize) {
          hasMore = false;
        } else {
          offset += batchSize;
        }
      }

      let playersArray = allPlayers;

      // Filter out drafted players using ref (current value at time of filtering)
      const currentDraftedIds = draftedIdsRef.current;
      if (currentDraftedIds && currentDraftedIds.length > 0) {
        playersArray = playersArray.filter(p => {
          const playerId = p?.ids?.league_player || p?.league_player_id;
          return !currentDraftedIds.includes(playerId);
        });
      }

      setPlayers(playersArray);

      // Pass players to parent for filter extraction using ref
      if (onPlayersLoadedRef.current) {
        onPlayersLoadedRef.current(playersArray);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load available players:', err);
      setPlayers([]);
      setError(err.message || 'Failed to load players');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [leagueId, filterPosition, filterSearch, filterSortBy, filterSortOrder]);

  // Load players on mount and when STABLE filter values change
  useEffect(() => {
    if (!leagueId) return;
    loadPlayers();
    initialLoadDoneRef.current = true;
  }, [loadPlayers, leagueId]);

  // Re-fetch when refreshTrigger changes (e.g., after pick undo)
  useEffect(() => {
    if (refreshTrigger > 0 && leagueId) {
      lastLoadParamsRef.current = null; // Force re-fetch even if params unchanged
      loadPlayers();
    }
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine which stats key to use based on year toggle
  const statsKey = statsYear === 'current' ? 'season' : `season_${previousSeason}`;

  // Create columns using shared draftColumns.js
  const columns = useMemo(() => {
    return createDraftAvailableColumns({
      onPlayerSelect,
      onViewProfile,
      onAddToQueue,
      statsKey,
      activeTab,
      scoringCategories
    });
  }, [onPlayerSelect, onViewProfile, onAddToQueue, statsKey, activeTab, scoringCategories]);

  // CRITICAL FIX: Reactively filter out drafted players
  // This useMemo reacts to draftedPlayerIds prop changes (when picks are made)
  const availablePlayers = useMemo(() => {
    if (!players || players.length === 0) return [];
    if (!draftedPlayerIds || draftedPlayerIds.length === 0) return players;

    return players.filter(p => {
      const playerId = p?.ids?.league_player || p?.league_player_id;
      return !draftedPlayerIds.includes(playerId);
    });
  }, [players, draftedPlayerIds]);

  // Filter players by position type (hitters vs pitchers) and minimum stats
  // Now uses availablePlayers (which excludes drafted players reactively)
  // Filter uses MAX of current and previous season stats so players qualify in either year
  const filteredByTab = useMemo(() => {
    if (!availablePlayers || availablePlayers.length === 0) return [];

    const hitterPositions = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH', 'UTIL', 'TWP'];

    // Stats keys for both years
    const currentStatsKey = 'season';
    const previousStatsKey = `season_${previousSeason}`;

    let filtered;
    if (activeTab === 'hitting') {
      // Include hitter positions + players with no/unknown position (so they're never invisible)
      filtered = availablePlayers.filter(p => {
        const pos = p?.info?.position;
        return !pos || hitterPositions.includes(pos) || !isRawPitcherPosition(pos);
      });
      // Apply min G filter for hitters - check BOTH seasons, use max
      if (filterMinG > 0) {
        filtered = filtered.filter(p => {
          const currentG = p?.stats?.[currentStatsKey]?.hitting?.games_played || 0;
          const previousG = p?.stats?.[previousStatsKey]?.hitting?.games_played || 0;
          const maxG = Math.max(currentG, previousG);
          return maxG >= filterMinG;
        });
      }
      // Apply min AB filter for hitters - check BOTH seasons, use max
      if (filterMinAB > 0) {
        filtered = filtered.filter(p => {
          const currentAB = p?.stats?.[currentStatsKey]?.hitting?.at_bats || 0;
          const previousAB = p?.stats?.[previousStatsKey]?.hitting?.at_bats || 0;
          const maxAB = Math.max(currentAB, previousAB);
          return maxAB >= filterMinAB;
        });
      }
    } else {
      filtered = availablePlayers.filter(p => isRawPitcherPosition(p?.info?.position));
      // Apply min G filter for pitchers - check BOTH seasons, use max
      if (filterMinGPitching > 0) {
        filtered = filtered.filter(p => {
          const currentG = p?.stats?.[currentStatsKey]?.pitching?.games || p?.stats?.[currentStatsKey]?.pitching?.games_played || 0;
          const previousG = p?.stats?.[previousStatsKey]?.pitching?.games || p?.stats?.[previousStatsKey]?.pitching?.games_played || 0;
          const maxG = Math.max(currentG, previousG);
          return maxG >= filterMinGPitching;
        });
      }
      // Apply min GS filter for pitchers - check BOTH seasons, use max
      if (filterMinGS > 0) {
        filtered = filtered.filter(p => {
          const currentGS = p?.stats?.[currentStatsKey]?.pitching?.games_started || 0;
          const previousGS = p?.stats?.[previousStatsKey]?.pitching?.games_started || 0;
          const maxGS = Math.max(currentGS, previousGS);
          return maxGS >= filterMinGS;
        });
      }
      // Apply min IP filter for pitchers - check BOTH seasons, use max
      if (filterMinIP > 0) {
        filtered = filtered.filter(p => {
          const currentIP = p?.stats?.[currentStatsKey]?.pitching?.innings_pitched || 0;
          const previousIP = p?.stats?.[previousStatsKey]?.pitching?.innings_pitched || 0;
          const maxIP = Math.max(currentIP, previousIP);
          return maxIP >= filterMinIP;
        });
      }
    }
    return filtered;
  }, [availablePlayers, activeTab, filterMinG, filterMinAB, filterMinGPitching, filterMinGS, filterMinIP, previousSeason]);


  // Sort the table data - single row per player (year toggle controls which stats show)
  // Also adds _displayRank for the Rank column
  const tableData = useMemo(() => {
    if (!sortConfig || !filteredByTab.length) return filteredByTab;

    const column = columns.find(col => col.key === sortConfig.key);

    const sorted = [...filteredByTab].sort((a, b) => {
      let aValue, bValue;

      if (column?.sortValue) {
        aValue = column.sortValue(a);
        bValue = column.sortValue(b);
      } else {
        aValue = getNestedValue(a, sortConfig.key);
        bValue = getNestedValue(b, sortConfig.key);
      }

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Try parsing as numbers
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const result = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? result : -result;
    });

    // Add display rank (1, 2, 3...) to each player after sorting
    return sorted.map((player, index) => ({
      ...player,
      _displayRank: index + 1
    }));
  }, [filteredByTab, sortConfig, columns]);

  // Handle sort click
  const handleSort = useCallback((columnKey) => {
    setSortConfig(prev => {
      if (prev?.key === columnKey) {
        if (prev.direction === 'asc') {
          return { key: columnKey, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return null;
        }
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  // Event delegation for player selection
  useEffect(() => {
    const handleClick = (e) => {
      console.log('[VirtualTable] Click detected, target:', e.target.tagName, e.target.className);
      const button = e.target.closest('[data-action="select-player"]');
      console.log('[VirtualTable] Button found:', button ? 'yes' : 'no', 'disabled:', button?.disabled);

      if (button && !button.disabled) {
        e.preventDefault();
        e.stopPropagation();

        const playerDataStr = button.getAttribute('data-player');
        console.log('[VirtualTable] Player data:', playerDataStr ? 'present' : 'missing', 'onPlayerSelect:', typeof onPlayerSelect);
        if (playerDataStr && onPlayerSelect) {
          try {
            const player = JSON.parse(playerDataStr);
            console.log('[VirtualTable] Calling onPlayerSelect for:', player.info?.full_name);
            onPlayerSelect(player);
          } catch (err) {
            console.error('Error parsing player data:', err);
          }
        }
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('click', handleClick, true);
      return () => container.removeEventListener('click', handleClick, true);
    }
  }, [onPlayerSelect]);

  // Reset scroll position when filters/tabs change
  // CRITICAL: Use primitive filter values, NOT the filters object (object reference changes on every render)
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(0);
    }
  }, [activeTab, filterPosition, filterSearch]);

  // Row height
  const ROW_HEIGHT = 32;

  // Calculate total table width
  const tableWidth = useMemo(() => {
    return columns.reduce((total, col) => total + (parseInt(col.width) || 100), 0);
  }, [columns]);

  // Get sort icon
  const getSortIcon = useCallback((columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return (
        <ChevronsUpDown
          className={`w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity ${dynastyTheme.classes.text.neutralLight}`}
        />
      );
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />
    ) : (
      <ChevronDown className={`w-3 h-3 ${dynastyTheme.classes.text.primary}`} />
    );
  }, [sortConfig]);

  // Row renderer - must be stable for react-window
  // Single row per player (year toggle controls which stats show)
  // Styling matches BoxScoreBatting pattern
  const Row = useCallback(({ index, style }) => {
    const player = tableData[index];
    if (!player) return null;

    // No alternating colors - matches DynastyTable which has transparent row backgrounds
    // The table container has the dark background already

    // Get column-specific text styling
    const getColumnTextClass = (columnKey) => {
      switch (columnKey) {
        case 'position':
          return `text-sm font-semibold ${dynastyTheme.classes.text.warning}`; // Orange for position
        case 'impact_rank':
        case 'baseline_price':
          return `font-bold ${dynastyTheme.classes.text.primary}`; // Yellow for rank/price
        case 'mlb_team':
          return `text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`; // Gray for team
        default:
          return dynastyTheme.classes.text.white; // White for everything else
      }
    };

    return (
      <div
        style={{
          ...style,
          display: 'flex',
          width: '100%',
          borderBottom: '1px solid rgba(202, 138, 4, 0.15)'
        }}
        className={`hover:${dynastyTheme.classes.bg.darkLighter} transition-colors`}
      >
        {columns.map((column, colIndex) => {
          const cellValue = column.render
            ? column.render(player)
            : (getNestedValue(player, column.key) ?? '-');

          const columnWidth = parseInt(column.width) || 100;
          const cellPadding = column.cellPadding || 'py-1 px-1';
          const textAlign = column.textAlign || 'text-center';
          const textClass = getColumnTextClass(column.key);

          // Left-justify player names, center everything else
          const justifyClass = column.key === 'player_name' ? 'justify-start pl-2' : 'justify-center';

          return (
            <div
              key={`${index}-${colIndex}`}
              style={{
                flex: `1 1 ${columnWidth}px`,
                minWidth: `${columnWidth}px`
              }}
              className={`${cellPadding} ${textAlign} ${textClass} flex items-center ${justifyClass} text-xs`}
            >
              {cellValue}
            </div>
          );
        })}
      </div>
    );
  }, [tableData, columns]);

  // Table header - matches DynastyTable styling
  const TableHeader = useCallback(() => (
    <div
      style={{
        display: 'flex',
        width: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}
      className={`${dynastyTheme.classes.bg.darkLighter} border-b-2 border-yellow-500`}
    >
      {columns.map((column, index) => {
        const columnWidth = parseInt(column.width) || 100;
        const isSortable = column.sortable !== false;
        const isCurrentSort = sortConfig?.key === column.key;

        return (
          <div
            key={index}
            onClick={() => isSortable && handleSort(column.key)}
            style={{
              flex: `1 1 ${columnWidth}px`,
              minWidth: `${columnWidth}px`
            }}
            className={`py-1 px-1 font-bold text-xs cursor-pointer select-none group
              transition-colors flex items-center justify-center ${isCurrentSort ? 'bg-yellow-400/10' : 'hover:bg-gray-600'}`}
          >
            <span className={`truncate transition-colors
              ${isCurrentSort ? dynastyTheme.classes.text.primaryBright : `${dynastyTheme.classes.text.primary} group-hover:text-yellow-300`}`}>
              {column.title || column.header}
            </span>
            {isSortable && (
              <span className="ml-0.5 flex-shrink-0">
                {getSortIcon(column.key)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  ), [columns, sortConfig, handleSort, getSortIcon]);

  // Loading state
  if (loading && players.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
        <p className="text-sm text-gray-400">Loading available players...</p>
      </div>
    );
  }

  // Error state
  if (error && players.length === 0) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8`}>
        <div className="text-center">
          <AlertCircle className={`w-12 h-12 mx-auto mb-4 text-red-500`} />
          <h3 className={`${dynastyTheme.components.heading.h3} mb-2 text-red-500`}>
            Error Loading Players
          </h3>
          <p className={dynastyTheme.classes.text.neutralLight}>
            {error}
          </p>
          <button
            onClick={loadPlayers}
            className={`mt-4 ${dynastyTheme.components.button.primary} px-4 py-2`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={tableContainerRef}>
      {/* Position Type Tabs */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all
              ${activeTab === 'hitting'
                ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
              }`}
          >
            Hitters ({availablePlayers.filter(p => { const pos = p?.info?.position; return !pos || !isRawPitcherPosition(pos); }).length})
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all
              ${activeTab === 'pitching'
                ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
              }`}
          >
            Pitchers ({availablePlayers.filter(p => isRawPitcherPosition(p?.info?.position)).length})
          </button>
        </div>

        {/* Year Toggle */}
        <div className="flex items-center gap-2">
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>Stats:</span>
          <div className="flex gap-1">
            <button
              onClick={() => setStatsYear('current')}
              className={`px-3 py-1 rounded text-xs font-medium transition-all
                ${statsYear === 'current'
                  ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                  : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                }`}
            >
              {currentSeason}
            </button>
            <button
              onClick={() => setStatsYear('previous')}
              className={`px-3 py-1 rounded text-xs font-medium transition-all
                ${statsYear === 'previous'
                  ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                  : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                }`}
            >
              {previousSeason}
            </button>
          </div>
          <span className={`${dynastyTheme.classes.text.neutralLight} text-xs ml-2`}>
            {filteredByTab.length} players
          </span>
        </div>
      </div>

      {/* Virtual Scrolling Table */}
      {tableData.length > 0 ? (
        <div style={{ width: '100%' }}>
          <TableHeader />

          <FixedSizeList
            ref={listRef}
            height={450}
            itemCount={tableData.length}
            itemSize={ROW_HEIGHT}
            width="100%"
            overscanCount={10}
            className="scrollbar-thin"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#ca8a04 transparent' }}
          >
            {Row}
          </FixedSizeList>
        </div>
      ) : (
        <div className="p-8 text-center">
          <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
          <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>
            No {activeTab} Found
          </h3>
          <p className={dynastyTheme.classes.text.neutralLight}>
            Try adjusting your search filters.
          </p>
        </div>
      )}

      {/* Footer info */}
      <div className={`mt-2 text-center`}>
        <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>
          Showing {statsYear === 'current' ? currentSeason : previousSeason} stats • Click player to select
        </span>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg z-50">
          <div className={`flex items-center gap-3 ${dynastyTheme.components.card.base} px-6 py-4 rounded-lg`}>
            <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`} />
            <span className={dynastyTheme.classes.text.white}>Loading players...</span>
          </div>
        </div>
      )}
    </div>
  );
});

VirtualAvailablePlayersTable.displayName = 'VirtualAvailablePlayersTable';

export default VirtualAvailablePlayersTable;
